"use client"

import { RefObject, useCallback, useEffect, useRef } from "react"
import { useVoiceTranscriptionStore } from "@/stores/use-voice-transcription-store"
import { Button, ButtonProps } from "@ui/shad"
import { Mic } from "lucide-react"

interface FocusableRef {
  focus: () => void
}

export interface VoiceMicButtonProps {
  /** Ref to the editor/input element to focus before recording */
  editorRef: RefObject<FocusableRef | HTMLElement | null>
  /**
   * When true, tracks locally whether THIS button initiated the recording.
   * Use this on pages with multiple editors so only the initiating button shows active state.
   */
  localActive?: boolean
  /** Button size - defaults to iconXs */
  size?: ButtonProps["size"]
  /** Additional class names */
  className?: string
  /** Whether the button is disabled */
  disabled?: boolean
  /** Icon size class - defaults to "h-4 w-4" */
  iconSize?: string
}

/**
 * Reusable microphone button for voice transcription.
 *
 * Usage:
 * ```tsx
 * const editorRef = useRef<EditorRefHandle>(null)
 * <VoiceMicButton editorRef={editorRef} />
 * ```
 *
 * For pages with multiple editors (like sequences), use localActive:
 * ```tsx
 * <VoiceMicButton editorRef={editorRef} localActive />
 * ```
 */
export function VoiceMicButton({
  editorRef,
  localActive = false,
  size = "iconSm",
  className,
  disabled,
}: VoiceMicButtonProps) {
  const isActiveRef = useRef(false)
  const { isActive: globalIsActive, trigger, stop } = useVoiceTranscriptionStore()

  useEffect(() => {
    if (!globalIsActive) {
      isActiveRef.current = false
    }
  }, [globalIsActive])

  const isActive = localActive ? globalIsActive && isActiveRef.current : globalIsActive

  const handleClick = useCallback(() => {
    if (isActive) {
      stop()
    } else {
      if (localActive) {
        isActiveRef.current = true
      }
      const ref = editorRef.current
      if (ref && "focus" in ref) {
        ref.focus()
      }
      setTimeout(() => trigger(), 50)
    }
  }, [isActive, localActive, stop, trigger, editorRef])

  return (
    <Button
      variant={isActive ? "secondary" : "ghost"}
      size={size}
      onClick={handleClick}
      disabled={disabled}
      className={className}
      type="button"
      tooltip={isActive ? "Stop dictation" : "Dictate"}
    >
      <Mic className={isActive ? `text-red-accent animate-pulse` : `text-icon`} />
    </Button>
  )
}
